import { useMemo } from 'react'
import { useActivities } from '../activity/useActivities'
import { useActivityLogs } from '../activity/useActivityLogs'
import { useAllBadges } from '../badges/useAllBadges'
import { useTeamBadges } from '../badges/useTeamBadges'
import { useChallenge } from '../challenge/useChallenge'
import { useChallengeBuckets } from '../challenge/useChallengeBuckets'
import { buildChallengeContext, teamSizeBoundsFromContext } from '../challenge/challengeContext'
import ActivityLogic from '../../logic/ActivityLogic'
import {
  getTeamSizeTiers,
  getTierForSize,
  type DifficultyTier,
} from '../../logic/teamSizeUtils'
import { buildActivityFeed, type ActivityFeedItem } from '../../logic/activityFeed'
import { buildTrophies, type TrophyBadgeItem } from '../../logic/trophies'
import type Team from '../../model/Team'
import type User from '../../model/User'
import { useTeam } from './useTeam'
import { useUser } from '../user/useUser'

export interface RosterMember {
  user: User
  rms: number
  isYou: boolean
  isLeader: boolean
}

export type { DifficultyTier }

export interface TeamScreenData {
  team: Team
  trophies: TrophyBadgeItem[]
  earnedCount: number
  roster: RosterMember[]
  feed: ActivityFeedItem[]
  activeTier: DifficultyTier
  teamSizeTiers: ReturnType<typeof getTeamSizeTiers>
  teamSizeMax: number
  canInvite: boolean
  canRename: boolean
}

function activeTierForTeam(
  team: Team,
  bounds: ReturnType<typeof teamSizeBoundsFromContext>,
): DifficultyTier {
  const size = team.targetMemberCount ?? team.members.length
  return getTierForSize(size, bounds)
}

export function useTeamScreenData(teamID: string): {
  data: TeamScreenData | null
  isLoading: boolean
  error: Error | null
} {
  const { data: user } = useUser()
  const { data: team, isLoading: teamLoading, error: teamError } = useTeam(teamID)
  const {
    data: challenge,
    isLoading: challengeLoading,
    error: challengeError,
  } = useChallenge(team?.challengeID ?? '')
  const {
    data: buckets,
    isLoading: bucketsLoading,
    error: bucketsError,
  } = useChallengeBuckets(team?.challengeID ?? '')
  const { data: teamBadges = [], isLoading: badgesLoading } = useTeamBadges(teamID)
  const { data: allBadges = [], isLoading: allBadgesLoading } = useAllBadges(team?.challengeID)
  const { data: activities, isLoading: activitiesLoading } = useActivities(team?.challengeID)
  const { data: activityLogs, isLoading: logsLoading } = useActivityLogs(teamID)

  const isLoading =
    teamLoading ||
    challengeLoading ||
    bucketsLoading ||
    badgesLoading ||
    allBadgesLoading ||
    activitiesLoading ||
    logsLoading

  const challengeContext = useMemo(() => {
    if (!challenge || !buckets?.length) return undefined
    return buildChallengeContext(challenge, buckets)
  }, [challenge, buckets])

  if (isLoading || !team || !challengeContext || !activities || !activityLogs || !user) {
    return { data: null, isLoading, error: (teamError ?? challengeError ?? bucketsError) as Error | null }
  }

  const bounds = teamSizeBoundsFromContext(challengeContext)
  const teamSizeTiers = getTeamSizeTiers(bounds)
  const teamSizeMax = challengeContext.teamSizeMax

  const trophies = buildTrophies(allBadges, teamBadges, 'team')

  const roster: RosterMember[] = [...team.members]
    .map((member) => ({
      user: member,
      rms: Math.round(ActivityLogic.getIndividualProgress(activityLogs, member.id)),
      isYou: member.id === user.id,
      isLeader: member.id === team.leaderID,
    }))
    .sort((a, b) => b.rms - a.rms)

  return {
    data: {
      team,
      trophies,
      earnedCount: trophies.filter((t) => t.earned).length,
      roster,
      feed: buildActivityFeed(activityLogs, activities, team.members),
      activeTier: activeTierForTeam(team, bounds),
      teamSizeTiers,
      teamSizeMax,
      canInvite: team.hasMember(user.id) && !team.isFull(teamSizeMax),
      canRename: team.hasMember(user.id) && team.leaderID === user.id,
    },
    isLoading: false,
    error: null,
  }
}
